<template>
  <main class="pr-4 !pb-0">

    <!-- 可拖拽悬浮按钮 -->
    <div
        class="fixed z-50 flex flex-col gap-4 cursor-move select-none"
        :style="{ bottom: '20px', left: '20px' }"
        @mousedown.prevent="startDrag"
    >
      <!-- 搜索同款 -->
      <button
          @click.stop="handleSearch"
          class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center space-x-2"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <span>搜索同款</span>
      </button>

      <!-- 全部无匹配 -->
      <button
          @click.stop="handleNoMatch('all')"
          class="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
      >
        无匹配
      </button>

      <!-- 单货号无匹配 -->
      <button
          @click.stop="handleNoMatch('single')"
          class="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition"
      >
        单货号无匹配
      </button>

      <!-- 刷新 -->
      <button
          @click.stop="handleRefresh"
          class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
      >
        刷新
      </button>
    </div>

    <!-- firstItem 图片展示 -->
    <div v-if="!loading && firstItem" class="flex justify-center mb-6">
      <div class="bg-white rounded-xl shadow-md overflow-hidden flex flex-col items-center p-4 w-full max-w-md">
        <div class="relative bg-gray-100 flex justify-center items-center w-full h-[260px]">
          <img :src="displayImage" class="object-contain rounded-lg max-h-full max-w-full"/>
        </div>

        <div class="p-4 text-center w-full space-y-2">
          <p class="text-md text-gray-700 mb-1">
            原始标题: <span class="font-bold text-lg">{{ firstItem.title || '暂无' }}</span>
          </p>
          <p class="text-md text-gray-700 mb-1">
            原始货号: <span class="font-bold text-lg">{{ firstItem.hh || '暂无' }}</span>
          </p>
          <p class="text-md text-gray-700 mb-1">
            原始颜色: <span class="font-bold text-lg">{{ firstItem.color || '暂无' }}</span>
          </p>
        </div>
      </div>
    </div>

    <!-- 全局加载中 -->
    <div v-if="loading" class="text-center mt-4 text-lg font-medium">
      正在加载商品详情...
    </div>

  </main>
</template>

<script>
import { getFirstDownloadUrlAndHh, sendNumberBack } from '@/api/dewu.js'

export default {
  name: 'SearchBar',
  props: {
    previewUrl: String
  },
  data() {
    return {
      loading: true,
      firstItem: null,
      picUrlFromQuery: this.previewUrl,

      topOffset: 200,
      rightOffset: 100,
      dragging: false,
      dragStartX: 0,
      dragStartY: 0,
      startTop: 0,
      startRight: 0
    }
  },

  watch: {
    previewUrl(newVal) {
      this.picUrlFromQuery = newVal
    }
  },

  computed: {
    displayImage() {
      return this.firstItem?.downloadUrl || this.picUrlFromQuery || ''
    }
  },

  async mounted() {
    await this.loadFirstItem()
    window.addEventListener('mousemove', this.onDrag)
    window.addEventListener('mouseup', this.stopDrag)
    this.loading = false
  },

  beforeUnmount() {
    window.removeEventListener('mousemove', this.onDrag)
    window.removeEventListener('mouseup', this.stopDrag)
  },

  methods: {
    /** 加载 firstItem */
    async loadFirstItem(retry = true) {
      try {
        const res = await getFirstDownloadUrlAndHh()

        if (!res) {
          console.warn('firstItem 接口返回 null')
          if (retry) {
            await new Promise(resolve => setTimeout(resolve, 1000))
            return this.loadFirstItem(false)
          }
          this.firstItem = null
          return
        }

        const newHh = res.hh || ''
        const oldHh = this.firstItem?.hh || ''

        this.firstItem = {
          downloadUrl: res.downloadUrl || '',
          hh: res.hh || '',
          color: res.color || '',
          title: res.title || '',
          spuId: res.spuId || 0,
          sent: false
        }

        this.picUrlFromQuery = null

        // 货号变化时通知父组件
        if ((oldHh && newHh !== oldHh) || (!oldHh && newHh)) {
          const downloadUrl = this.firstItem.downloadUrl
          this.$emit('hh-changed', { downloadUrl })
        }
      } catch (e) {
        console.error('加载 firstItem 失败:', e)
        this.firstItem = null
      }
    },

    /** 搜索按钮 */
    handleSearch() {
      this.$emit('search', this.displayImage)
    },

    /** 刷新 firstItem */
    async handleRefresh() {
      this.picUrlFromQuery = null
      await this.loadFirstItem()
    },

    /** 无匹配统一方法 */
    async handleNoMatch(type) {
      const wxLoginToken = localStorage.getItem('wxLoginToken')
      if (!wxLoginToken) return alert('请先设置 wxLoginToken')

      let hh = ''
      let remark = '无匹配'

      if (type === 'single') {
        if (!this.firstItem?.hh) return alert('当前货号为空，无法提交单货号无匹配')
        hh = this.firstItem.hh
        remark = '单货号无匹配'
      }

      try {
        await sendNumberBack(hh, remark, wxLoginToken)
        await new Promise(resolve => setTimeout(resolve, 1000))
        await this.handleRefresh()
      } catch (e) {
        console.error(e)
        alert(`${remark} 提交失败`)
      }
    },

    /** 拖拽开始 */
    startDrag(event) {
      this.dragging = true
      this.dragStartX = event.clientX
      this.dragStartY = event.clientY
      this.startTop = this.topOffset
      this.startRight = this.rightOffset
    },

    /** 拖拽中 */
    onDrag(event) {
      if (!this.dragging) return
      const deltaX = event.clientX - this.dragStartX
      const deltaY = event.clientY - this.dragStartY
      this.topOffset = this.startTop + deltaY
      this.rightOffset = Math.max(0, this.startRight - deltaX)
    },

    /** 拖拽结束 */
    stopDrag() {
      this.dragging = false
    }
  }
}
</script>

<style>
body.dragging {
  user-select: none;
}
</style>
